import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import type { Position } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useLocalStorage } from '@sorare/core/src/hooks/useLocalStorage';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { isHitter, isPitcher } from '@sorare/core/src/lib/baseball';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GameBoxPlayersTable } from './GameBoxPlayersTable/index.tsx';
import type {
  GameBoxScore_BaseballPlayer,
  GameBoxScore_GameOfBaseball,
  GameBoxScore_So5Appearance,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  teamHitting: {
    id: 'GameBoxScore.teamHitting',
    defaultMessage: '{team} Hitting',
  },
  teamPitching: {
    id: 'GameBoxScore.teamPitching',
    defaultMessage: '{team} Pitching',
  },
});

const Wrapper = styled(Vertical).attrs({ gap: 0 })`
  --current-background-color: var(--c-black);
  .highlighted {
    --current-background-color: var(--c-nd-50);
  }
`;

const StickyHeaderWrapper = styled(Horizontal)`
  padding: var(--triple-unit) var(--double-unit) var(--intermediate-unit);
`;

const FantasyScoreViewSwitch = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--unit);
  padding: 0;
  margin: var(--unit) auto 0;

  @media ${desktopAndAbove} {
    margin: 0;
  }
`;

const Logo = styled.img`
  width: 24px;
  height: 24px;
`;

type Props = {
  game: GameBoxScore_GameOfBaseball;
  homeTeamPlayers: GameBoxScore_BaseballPlayer[];
  awayTeamPlayers: GameBoxScore_BaseballPlayer[];
};

/**
 * This function segregates an array of baseball players into two separate arrays:
 * one for pitchers and another for batters.
 */
const getPitchersAndBatters = <
  T extends { anyPlayer: { anyPositions: Position[] } },
>(
  cards: T[]
): [T[], T[]] => {
  return cards.reduce(
    (rest, card) => {
      let [pitchers, batters] = rest;
      if (isPitcher(card.anyPlayer.anyPositions)) {
        pitchers = [...pitchers, card];
      }
      if (isHitter(card.anyPlayer.anyPositions)) {
        batters = [...batters, card];
      }
      return [pitchers, batters];
    },
    [[], []] as [T[], T[]]
  );
};

/**
 * This function renders a table of baseball players based on the selected team and player position.
 * Depending on the `position` parameter, the function will either display pitchers or batters.
 */
const renderPlayersTable = (
  position: 'pitching' | 'batting',
  game: GameBoxScore_GameOfBaseball,
  fantasyScore: boolean,
  homeTeamPlayers: {
    player?: GameBoxScore_BaseballPlayer;
    cards?: GameBoxScore_So5Appearance[];
  }[],
  awayTeamPlayers?: {
    player?: GameBoxScore_BaseballPlayer;
    cards?: GameBoxScore_So5Appearance[];
  }[]
) => {
  if (!game.homeTeam && !game.awayTeam) return null;

  const playersMap =
    position === 'pitching'
      ? [...homeTeamPlayers, ...(awayTeamPlayers ?? [])]
      : homeTeamPlayers.concat(awayTeamPlayers ?? []);

  return (
    <GameBoxPlayersTable
      game={game}
      position={position}
      fantasyScore={fantasyScore}
      cardsInLineup={playersMap}
    />
  );
};

export const GameBoxScore = ({
  game,
  homeTeamPlayers,
  awayTeamPlayers,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const [displayScore, setDisplayScore] = useLocalStorage(
    'mlbGameViewStats',
    'fantasy'
  );

  const cardsInLineups = groupBy(
    game?.mySo5Appearances?.flatMap(appearance => appearance) || [],
    card => card?.anyPlayer?.slug
  );

  const homeTeamPlayersWithCards = useMemo(() => {
    return homeTeamPlayers.map(anyPlayer => ({
      anyPlayer,
      cards: cardsInLineups[anyPlayer.slug],
    }));
  }, [homeTeamPlayers, cardsInLineups]);

  const awayTeamPlayersWithCards = useMemo(() => {
    return awayTeamPlayers.map(anyPlayer => ({
      anyPlayer,
      cards: cardsInLineups[anyPlayer.slug],
    }));
  }, [awayTeamPlayers, cardsInLineups]);

  const [homeTeamPitchers, homeTeamBatters] = getPitchersAndBatters(
    homeTeamPlayersWithCards
  );
  const [awayTeamPitchers, awayTeamBatters] = getPitchersAndBatters(
    awayTeamPlayersWithCards
  );

  const newSections = [
    {
      key: 'awayTeamPitching',
      logo: game.awayTeam?.pictureUrl,
      title: formatMessage(messages.teamPitching, {
        team: game.awayTeam?.shortName,
      }),
      content: renderPlayersTable(
        'pitching',
        game,
        displayScore === 'fantasy',
        awayTeamPitchers
      ),
    },
    {
      key: 'homeTeamPitching',
      logo: game.homeTeam?.pictureUrl,
      title: formatMessage(messages.teamPitching, {
        team: game.homeTeam?.shortName,
      }),
      content: renderPlayersTable(
        'pitching',
        game,
        displayScore === 'fantasy',
        homeTeamPitchers
      ),
    },
    {
      key: 'awayTeamHitting',
      logo: game.awayTeam?.pictureUrl,
      title: formatMessage(messages.teamHitting, {
        team: game.awayTeam?.shortName,
      }),
      content: renderPlayersTable(
        'batting',
        game,
        displayScore === 'fantasy',
        awayTeamBatters
      ),
    },
    {
      key: 'homeTeamHitting',
      logo: game.homeTeam?.pictureUrl,
      title: formatMessage(messages.teamHitting, {
        team: game.homeTeam?.shortName,
      }),
      content: renderPlayersTable(
        'batting',
        game,
        displayScore === 'fantasy',
        homeTeamBatters
      ),
    },
  ];

  return (
    <Wrapper>
      <ContentWithStickyHeader
        header={
          <Horizontal center={false}>
            <FantasyScoreViewSwitch>
              <TabBar value={displayScore} variant="primary">
                <Tab
                  key="fantasy"
                  value="fantasy"
                  onClick={() => setDisplayScore('fantasy')}
                >
                  <FormattedMessage
                    id="GaneBoxScore.fantasyScoreView"
                    defaultMessage="Fantasy points"
                  />
                </Tab>
                <Tab
                  key="actual"
                  value="actual"
                  onClick={() => setDisplayScore('actual')}
                >
                  <FormattedMessage
                    id="GaneBoxScore.gameStatsView"
                    defaultMessage="Game stats"
                  />
                </Tab>
              </TabBar>
            </FantasyScoreViewSwitch>
          </Horizontal>
        }
      >
        <Vertical gap={3}>
          {newSections.map(({ logo, title, content, key }) => (
            <ContentWithStickyHeader
              key={key}
              header={
                <StickyHeaderWrapper>
                  {!!logo && <Logo src={logo} />}
                  <BodyM bold>{title}</BodyM>
                </StickyHeaderWrapper>
              }
            >
              {content}
            </ContentWithStickyHeader>
          ))}
        </Vertical>
      </ContentWithStickyHeader>
    </Wrapper>
  );
};

GameBoxScore.fragments = {
  BaseballPlayer: gql`
    fragment GameBoxScore_BaseballPlayer on BaseballPlayer {
      slug
      anyPositions
      ...GameBoxPlayersTable_BaseballPlayer
    }
    ${GameBoxPlayersTable.fragments.BaseballPlayer}
  ` as TypedDocumentNode<GameBoxScore_BaseballPlayer>,
  So5Appearance: gql`
    fragment GameBoxScore_So5Appearance on So5Appearance {
      id
      ...GameBoxPlayersTable_So5Appearance
    }
    ${GameBoxPlayersTable.fragments.So5Appearance}
  ` as TypedDocumentNode<GameBoxScore_So5Appearance>,
  GameOfBaseball: gql`
    fragment GameBoxScore_GameOfBaseball on GameOfBaseball {
      id
      awayTeam {
        slug
        name
        shortName
        pictureUrl
      }
      homeTeam {
        slug
        name
        shortName
        pictureUrl
      }
      mySo5Appearances {
        id
        anyPlayer {
          slug
        }
        ...GameBoxPlayersTable_So5Appearance
      }
      ...GameBoxPlayersTable_GameOfBaseball
    }
    ${GameBoxPlayersTable.fragments.So5Appearance}
    ${GameBoxPlayersTable.fragments.GameOfBaseball}
  ` as TypedDocumentNode<GameBoxScore_GameOfBaseball>,
};
